import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, Eye, EyeOff, Mail, Monitor, Smartphone } from 'lucide-react';
import { isSyntheticEmail, NOTIFICATION_EVENT_KINDS } from '@erp/shared';
import type {
  NotificationPrefs,
  PasswordChangeBody,
  Profile,
  ProfilePatchBody,
  UserSession,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { updateStoredUser } from '@/auth/use-auth';
import { performLogout } from '@/auth/logout';
import { getKeycloak } from '@/auth/keycloak';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { errorMessage } from '@/features/clients/clients-queries';
import {
  describeUserAgent,
  formatDateTime,
  notificationPrefsQueryOptions,
  profileQueryOptions,
  sessionsQueryOptions,
  twoFactorQuery,
} from './account-queries';
import { requestBrowserNotificationPermission } from '@/shared/browser-notifications';
import { previewNotificationDing } from '@/shared/notification-sound';
import { NotificationPrefsTable } from './notification-prefs-table';
import type { AccountTab } from './account-search';

const TAB_TRIGGER_CLASSES =
  'h-10 flex-none rounded-none px-1 text-sm font-medium data-[state=active]:text-primary after:h-0.5 after:bg-primary group-data-[orientation=horizontal]/tabs:after:bottom-0';

/**
 * Minha conta (estilo Tiflux): perfil, segurança (senha + 2FA), sessões web e
 * preferências de notificação do sino.
 */
export function AccountPage() {
  // a aba vive na URL (spec 037): o "Configurações" do sino chega em Notificações
  const search = useSearch({ from: '/_authenticated/account' });
  const navigate = useNavigate();
  const tab: AccountTab = search.tab ?? 'conta';
  const setTab = (next: string) =>
    void navigate({
      to: '/account',
      search: { tab: next === 'conta' ? undefined : (next as AccountTab) },
      replace: true,
    });

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <div className="rounded-lg border bg-card shadow-xs">
          <div className="px-4 pt-3">
            <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
              <span>Configurações</span>
              <span className="mx-1.5">/</span>
              <span className="text-foreground">Minha conta</span>
            </nav>
            <h1 className="mt-2 text-xl leading-7 font-semibold">Minha conta</h1>
          </div>
          <TabsList
            variant="line"
            className="mt-1 w-full justify-start gap-6 rounded-none p-0 px-4 group-data-[orientation=horizontal]/tabs:h-11"
          >
            <TabsTrigger value="conta" className={TAB_TRIGGER_CLASSES}>
              Minha conta
            </TabsTrigger>
            <TabsTrigger value="seguranca" className={TAB_TRIGGER_CLASSES}>
              Segurança
            </TabsTrigger>
            <TabsTrigger value="sessoes" className={TAB_TRIGGER_CLASSES}>
              Sessões
            </TabsTrigger>
            <TabsTrigger value="notificacoes" className={TAB_TRIGGER_CLASSES}>
              Notificações
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="conta">
          <ProfileTab />
        </TabsContent>
        <TabsContent value="seguranca">
          <SecurityTab />
        </TabsContent>
        <TabsContent value="sessoes">
          <SessionsTab />
        </TabsContent>
        <TabsContent value="notificacoes">
          <NotificationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Aba "Minha conta" ───────────────────────────────────────────────────────

function ProfileTab() {
  const profileQ = useQuery(profileQueryOptions());

  if (profileQ.isPending) {
    return (
      <div className="rounded-lg border bg-card p-4 shadow-xs" aria-busy>
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="flex w-48 flex-col items-center gap-3">
            <Skeleton className="size-32 rounded-full" />
            <Skeleton className="h-3.5 w-20" />
          </div>
          <div className="grid flex-1 content-start gap-4 sm:grid-cols-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3.5 w-16" />
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (profileQ.isError) {
    return (
      <div className="rounded-lg border bg-card shadow-xs">
        <EmptyState message={`Erro ao carregar perfil: ${errorMessage(profileQ.error)}`} />
      </div>
    );
  }

  return <ProfileForm profile={profileQ.data} />;
}

function ProfileForm({ profile }: { profile: Profile }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(profile.name);
  const [email, setEmail] = useState(profile.email);
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [extension, setExtension] = useState(profile.extension ?? '');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 4000);
    return () => clearTimeout(t);
  }, [saved]);

  const save = useMutation({
    mutationFn: async (body: ProfilePatchBody) => {
      const res = await api<Profile>('/me', { method: 'PATCH', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(profileQueryOptions().queryKey, updated);
      // Sidebar/topbar leem o usuário salvo no localStorage.
      updateStoredUser({ name: updated.name, email: updated.email });
      setSaved(true);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    save.mutate({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      extension: extension.trim() || null,
    });
  };

  const initial = (name.trim().charAt(0) || '?').toUpperCase();

  return (
    <form onSubmit={submit} className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="flex flex-col gap-6 sm:flex-row">
        {/* Foto de perfil */}
        <div className="flex w-48 shrink-0 flex-col items-center gap-3 pt-1">
          <div
            aria-hidden
            className="flex size-32 items-center justify-center rounded-full bg-primary text-[40px] font-semibold text-primary-foreground select-none"
          >
            {initial}
          </div>
          <span className="text-[13px] text-muted-foreground">Foto de perfil</span>
        </div>

        {/* Dados */}
        <div className="min-w-0 flex-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="account-name">
                Nome <span className="text-destructive">*</span>
              </Label>
              <Input
                id="account-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-email">
                E-mail <span className="text-destructive">*</span>
              </Label>
              <Input
                id="account-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-phone">Telefone</Label>
              <Input
                id="account-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(00) 00000-0000"
                inputMode="tel"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-extension">Ramal</Label>
              <Input
                id="account-extension"
                value={extension}
                onChange={(e) => setExtension(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-language">Idioma</Label>
              <Select id="account-language" value="pt-BR" disabled onChange={() => undefined}>
                <option value="pt-BR">Português</option>
              </Select>
            </div>
          </div>

          {save.error ? (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {errorMessage(save.error)}
            </p>
          ) : null}
          {saved ? (
            <p role="status" className="mt-3 flex items-center gap-1.5 text-sm text-success">
              <CircleCheck className="size-4" />
              Alterações salvas com sucesso.
            </p>
          ) : null}

          <div className="mt-4 flex justify-end border-t border-divider pt-4">
            <Button
              type="submit"
              size="sm"
              disabled={save.isPending || !name.trim() || !email.trim()}
            >
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}

// ── Aba "Segurança" ─────────────────────────────────────────────────────────

function PasswordInput({
  id,
  label,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label} <span className="text-destructive">*</span>
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required
          className="pr-9"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute top-0 right-0 flex h-9 items-center pr-3 text-muted-foreground transition-colors hover:text-foreground"
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </div>
  );
}

function TwoFactorCard({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: typeof Mail;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary-pressed">
          <Icon className="size-4.5" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold">{title}</div>
          <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>
        </div>
      </div>
      {action ?? (
        <Button type="button" size="sm" disabled title="Em breve" className="w-fit">
          Ativar autenticação
        </Button>
      )}
    </div>
  );
}

/** Card do TOTP: ativa via AIA do Keycloak (kc_action=CONFIGURE_TOTP); o
 *  Keycloak (com o nosso tema) mostra o QR code e valida o primeiro código. */
function TotpCard() {
  const queryClient = useQueryClient();
  const statusQ = useQuery(twoFactorQuery());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const disable = useMutation({
    mutationFn: async () => {
      const res = await api<void>('/me/2fa/totp', { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['erp', 'account', '2fa'] });
    },
  });

  const status = statusQ.data;
  let action: ReactNode;
  if (!status || !status.supported) {
    action = undefined; // fallback "Em breve" (modo local)
  } else if (status.totpConfigured) {
    action = (
      <div className="flex items-center gap-2">
        <Badge variant="success">Ativada</Badge>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-fit border-destructive/40 text-destructive hover:bg-destructive/5"
          onClick={() => setConfirmOpen(true)}
        >
          Desativar
        </Button>
      </div>
    );
  } else {
    action = (
      <Button
        type="button"
        size="sm"
        className="w-fit"
        onClick={() => getKeycloak().login({ action: 'CONFIGURE_TOTP' })}
      >
        Ativar autenticação
      </Button>
    );
  }

  return (
    <>
      <TwoFactorCard
        icon={Smartphone}
        title="Google Authenticator"
        description="Use o aplicativo para gerar códigos de verificação no seu celular."
        action={action}
      />
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Desativar 2FA">
        <p className="text-sm text-muted-foreground">
          Sua conta deixará de pedir o código do aplicativo autenticador no login. Deseja continuar?
        </p>
        {disable.error ? (
          <p className="mt-2 text-sm text-destructive">
            Não foi possível desativar. Tente de novo.
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={disable.isPending}
            onClick={() => disable.mutate()}
          >
            {disable.isPending ? 'Desativando…' : 'Desativar'}
          </Button>
        </div>
      </Dialog>
    </>
  );
}

function SecurityTab() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    if (!updated) return;
    const t = setTimeout(() => setUpdated(false), 4000);
    return () => clearTimeout(t);
  }, [updated]);

  const change = useMutation({
    mutationFn: async (body: PasswordChangeBody) => {
      const res = await api<undefined>('/me/password', { method: 'POST', body });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setUpdated(true);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!currentPassword || newPassword.length < 6) return;
    change.mutate({ currentPassword, newPassword });
  };

  const changeError = change.error
    ? errorMessage(change.error) === 'wrong_password'
      ? 'Senha atual incorreta'
      : errorMessage(change.error)
    : null;

  return (
    <div className="space-y-4">
      {/* Alterar senha */}
      <form onSubmit={submit} className="rounded-lg border bg-card p-4 shadow-xs">
        <h2 className="text-base font-semibold">Alterar senha</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Sua sessão atual continuará ativa após a atualização de senha.
        </p>
        <div className="mt-4 grid max-w-2xl gap-4 sm:grid-cols-2">
          <PasswordInput
            id="account-current-password"
            label="Senha atual"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
          />
          <PasswordInput
            id="account-new-password"
            label="Nova senha"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
          />
        </div>
        {changeError ? (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {changeError}
          </p>
        ) : null}
        {updated ? (
          <p role="status" className="mt-3 flex items-center gap-1.5 text-sm text-success">
            <CircleCheck className="size-4" />
            Senha atualizada com sucesso.
          </p>
        ) : null}
        <div className="mt-4">
          <Button
            type="submit"
            size="sm"
            disabled={change.isPending || !currentPassword || newPassword.length < 6}
          >
            {change.isPending ? 'Atualizando…' : 'Atualizar senha'}
          </Button>
        </div>
      </form>

      {/* Autenticação de dois fatores */}
      <section className="rounded-lg border bg-card p-4 shadow-xs">
        <h2 className="text-base font-semibold">Autenticação de dois fatores</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Adicione uma camada extra de segurança ao acessar sua conta.
        </p>
        <div className="mt-4 grid max-w-3xl gap-4 sm:grid-cols-2">
          <TwoFactorCard
            icon={Mail}
            title="E-mail"
            description="Receba um código de verificação no seu e-mail a cada novo acesso."
          />
          <TotpCard />
        </div>
      </section>
    </div>
  );
}

// ── Aba "Sessões" ───────────────────────────────────────────────────────────

function SessionCard({ session, onRevoke }: { session: UserSession; onRevoke: () => void }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-start gap-3">
        <Monitor className="size-8 shrink-0 text-primary" strokeWidth={1.5} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{describeUserAgent(session.userAgent)}</span>
            {session.current ? <Badge variant="success">Sessão atual</Badge> : null}
          </div>
          <div className="mt-1 space-y-0.5 text-[13px] text-muted-foreground">
            <div>IP: {session.ip ?? '—'}</div>
            <div>Data da sessão: {formatDateTime(session.createdAt)}</div>
          </div>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRevoke}
        className="w-fit border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
      >
        Encerrar sessão
      </Button>
    </div>
  );
}

function SessionsTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sessionsQ = useQuery(sessionsQueryOptions());
  const [toRevoke, setToRevoke] = useState<UserSession | null>(null);

  const revoke = useMutation({
    mutationFn: async (session: UserSession) => {
      const res = await api<undefined>(`/me/sessions/${session.id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
      return session;
    },
    onSuccess: (session) => {
      setToRevoke(null);
      if (session.current) {
        // Encerrou a própria sessão: sai e volta pro login (no modo keycloak,
        // roteia pelo end-session do Keycloak).
        performLogout(() => void navigate({ to: '/login' }));
        return;
      }
      void queryClient.invalidateQueries({ queryKey: sessionsQueryOptions().queryKey });
    },
  });

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <h2 className="text-base font-semibold">Sessões Web</h2>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Dispositivos conectados à sua conta. Encerre as sessões que você não reconhece.
      </p>

      {sessionsQ.isPending ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      ) : sessionsQ.isError ? (
        <EmptyState message={`Erro ao carregar sessões: ${errorMessage(sessionsQ.error)}`} />
      ) : sessionsQ.data.length === 0 ? (
        <EmptyState message="Não há dados" />
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {sessionsQ.data.map((session) => (
            <SessionCard key={session.id} session={session} onRevoke={() => setToRevoke(session)} />
          ))}
        </div>
      )}

      {toRevoke ? (
        <Dialog open onClose={() => setToRevoke(null)} title="Encerrar sessão">
          <p className="text-sm text-muted-foreground">
            {toRevoke.current
              ? 'Esta é a sua sessão atual. Ao encerrá-la você será desconectado e voltará para a tela de login.'
              : `Encerrar a sessão "${describeUserAgent(toRevoke.userAgent)}"? O dispositivo precisará fazer login novamente.`}
          </p>
          {revoke.error ? (
            <p role="alert" className="mt-2 text-sm text-destructive">
              {errorMessage(revoke.error)}
            </p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setToRevoke(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate(toRevoke)}
            >
              {revoke.isPending ? 'Encerrando…' : 'Encerrar sessão'}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

// ── Aba "Notificações" ──────────────────────────────────────────────────────

/**
 * A API de antes (o SPA sobe no Pages minutos antes da API) não conhece as
 * células de e-mail e responde 422 `invalid_pref_keys`: a tela diz o que
 * aconteceu em vez do código, e a mudança volta (o PUT é otimista).
 */
function prefsSaveError(err: unknown): string {
  if (err !== null && typeof err === 'object' && (err as { error?: unknown }).error === 'invalid_pref_keys') {
    return 'As preferências de e-mail ainda estão sendo atualizadas no servidor. Tente de novo em alguns minutos.';
  }
  return errorMessage(err);
}

function NotificationsTab() {
  const queryClient = useQueryClient();
  const prefsQ = useQuery(notificationPrefsQueryOptions());
  const profileQ = useQuery(profileQueryOptions());
  const prefsKey = notificationPrefsQueryOptions().queryKey;

  const save = useMutation({
    mutationFn: async (next: NotificationPrefs) => {
      const res = await api<NotificationPrefs>('/me/notification-prefs', {
        method: 'PUT',
        body: next,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    // Otimista: aplica localmente e reverte se o PUT falhar.
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: prefsKey });
      const previous = queryClient.getQueryData(prefsKey);
      queryClient.setQueryData(prefsKey, next);
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.previous !== undefined) queryClient.setQueryData(prefsKey, ctx.previous);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: prefsKey }),
  });

  // Notificação do navegador: a permissão é pedida AQUI, dentro do clique —
  // nunca no load da página. Negada/indisponível ⇒ a preferência NÃO liga
  // (ligar sem permissão só criaria um switch que não faz nada).
  const [browserNotice, setBrowserNotice] = useState<string | null>(null);
  const toggleBrowser = async (checked: boolean) => {
    setBrowserNotice(null);
    if (!checked) {
      save.mutate({ ...(prefsQ.data ?? {}), browserEnabled: false });
      return;
    }
    const permission = await requestBrowserNotificationPermission();
    if (permission === 'unsupported') {
      setBrowserNotice('Este navegador não suporta notificações na área de trabalho.');
      return;
    }
    if (permission !== 'granted') {
      setBrowserNotice(
        'Permissão negada pelo navegador. Libere as notificações para este site nas configurações do navegador e tente de novo.'
      );
      return;
    }
    save.mutate({ ...(prefsQ.data ?? {}), browserEnabled: true });
  };

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <h2 className="text-base font-semibold">Preferências de notificação</h2>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Escolha o que chega no sino e o que chega por e-mail, evento por evento.
      </p>

      {prefsQ.isPending ? (
        <div className="mt-4 max-w-2xl space-y-3" aria-busy>
          {Array.from({ length: NOTIFICATION_EVENT_KINDS.length * 2 + 1 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : prefsQ.isError ? (
        <EmptyState message={`Erro ao carregar preferências: ${errorMessage(prefsQ.error)}`} />
      ) : (
        <div className="mt-4 max-w-2xl">
          {/* spec 037 (PR B): Notificações × E-mails, cada evento em geral e nos meus tickets */}
          <NotificationPrefsTable prefs={prefsQ.data ?? {}} onSave={(next) => save.mutate(next)} />
          <p className="mt-3 text-[13px] text-muted-foreground">
            <strong className="font-medium text-foreground">Geral</strong> é todo ticket que você enxerga;{' '}
            <strong className="font-medium text-foreground">nos meus tickets</strong>, os em que você é o
            responsável. Uma notificação gera um e-mail só, mesmo marcada nas duas linhas, e nada chega do que
            você mesmo fez.
            {profileQ.data?.email ? (
              isSyntheticEmail(profileQ.data.email) ? (
                <span className="mt-1 block text-destructive">
                  O seu endereço ({profileQ.data.email}) não recebe e-mail: as notificações por e-mail não saem
                  para ele.
                </span>
              ) : (
                <span className="mt-1 block">Os e-mails vão para {profileQ.data.email}.</span>
              )
            ) : null}
          </p>

          {/* Alertas in-app (v12): som e notificação do navegador. */}
          <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-t border-divider pt-4">
            <div className="min-w-0">
              <span className="block text-sm font-medium">Som ao receber notificação</span>
              <span className="mt-0.5 block text-[13px] text-muted-foreground">
                Toca um aviso curto quando chega uma notificação nova (no máximo um a cada 3
                segundos).
              </span>
            </div>
            <span className="flex w-24 justify-center">
              <Switch
                checked={prefsQ.data?.soundEnabled !== false}
                onCheckedChange={(checked) => {
                  // Ligar dentro do clique = gesto do usuário: aproveita para
                  // destravar o AudioContext e já dar o preview do som.
                  if (checked) previewNotificationDing();
                  save.mutate({ ...(prefsQ.data ?? {}), soundEnabled: checked });
                }}
                aria-label="Som ao receber notificação"
              />
            </span>
          </div>

          <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-t border-divider pt-4">
            <div className="min-w-0">
              <span className="block text-sm font-medium">Notificações do navegador</span>
              <span className="mt-0.5 block text-[13px] text-muted-foreground">
                Mostra a notificação na área de trabalho mesmo com o ERP em outra aba. A permissão é
                pedida só ao ligar esta opção.
              </span>
              {browserNotice ? (
                <span role="status" className="mt-1 block text-[13px] text-destructive">
                  {browserNotice}
                </span>
              ) : null}
            </div>
            <span className="flex w-24 justify-center">
              <Switch
                checked={prefsQ.data?.browserEnabled === true}
                onCheckedChange={(checked) => void toggleBrowser(checked)}
                aria-label="Notificações do navegador"
              />
            </span>
          </div>

          {save.error ? (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {prefsSaveError(save.error)}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}
